// src/errors/codes.ts
var XONE_ERROR_CODES = {
  FORBIDDEN_GLOBAL: "XONE.JS.FORBIDDEN_GLOBAL",
  PARSE_ERROR: "XONE.JS.PARSE_ERROR",
  RUNTIME_ERROR: "XONE.JS.RUNTIME_ERROR",
  SCOPE_INVALID: "XONE.JS.SCOPE_INVALID"
};

// src/errors/XoneScriptError.ts
var XoneScriptError = class _XoneScriptError extends Error {
  code;
  script;
  cause;
  constructor(message, options) {
    super(message);
    this.name = "XoneScriptError";
    this.code = options.code;
    this.script = options.script === void 0 ? void 0 : options.script.length > 200 ? options.script.slice(0, 200) + "..." : options.script;
    this.cause = options.cause;
    Object.setPrototypeOf(this, _XoneScriptError.prototype);
  }
};

// src/realm/AsyncFunctionRealmAdapter.ts
var AsyncFunction = Object.getPrototypeOf(
  async function() {
  }
).constructor;
var AsyncFunctionRealmAdapter = class {
  includeSources;
  constructor(includeSources) {
    this.includeSources = includeSources;
  }
  async execute(source, scopeProxy) {
    const includesBlock = this.includeSources.join("\n;\n");
    const body = `
            return (async () => {
                with (__xoneScope__) {
                    ${includesBlock}
                    ;
                    ${source}
                }
            })();
        `;
    let fn;
    try {
      fn = new AsyncFunction("__xoneScope__", body);
    } catch (err) {
      const cause = err instanceof Error ? err : new Error(String(err));
      throw new XoneScriptError(
        `Error al compilar el script XOne: ${cause.message}`,
        {
          code: XONE_ERROR_CODES.PARSE_ERROR,
          script: source,
          cause
        }
      );
    }
    try {
      return await fn(scopeProxy);
    } catch (err) {
      if (err instanceof XoneScriptError) {
        throw err;
      }
      if (err instanceof ReferenceError) {
        const m = /'?([A-Za-z_$][\w$]*)'? is not defined/.exec(err.message);
        if (m) {
          throw new XoneScriptError(
            `Acceso a identificador no definido o prohibido: "${m[1]}".`,
            {
              code: XONE_ERROR_CODES.FORBIDDEN_GLOBAL,
              script: source,
              cause: err
            }
          );
        }
      }
      const cause = err instanceof Error ? err : new Error(String(err));
      throw new XoneScriptError(
        `Error en tiempo de ejecucion: ${cause.message}`,
        {
          code: XONE_ERROR_CODES.RUNTIME_ERROR,
          script: source,
          cause
        }
      );
    }
  }
};

// src/realm/ShadowRealmAdapter.ts
var ShadowRealmAdapter = class {
  realm;
  constructor() {
    const g = globalThis;
    this.realm = typeof g.ShadowRealm === "function" ? new g.ShadowRealm() : null;
  }
  isAvailable() {
    return this.realm !== null;
  }
  async execute(source, _scopeProxy) {
    if (!this.realm) {
      throw new XoneScriptError(
        "ShadowRealm no esta disponible en este entorno. Use AsyncFunctionRealmAdapter o cargue el polyfill @endo/shadow-realm.",
        {
          code: XONE_ERROR_CODES.RUNTIME_ERROR,
          script: source
        }
      );
    }
    throw new XoneScriptError(
      "ShadowRealmAdapter no esta implementado en fase 1. Use AsyncFunctionRealmAdapter.",
      {
        code: XONE_ERROR_CODES.RUNTIME_ERROR,
        script: source
      }
    );
  }
};

// src/globals/registerGlobals.ts
function buildGlobalsMap(globals) {
  const m = /* @__PURE__ */ new Map();
  m.set("ui", globals.ui);
  m.set("appData", globals.appData);
  m.set("$http", globals.$http);
  m.set("crypto", globals.crypto);
  m.set("console", globals.console);
  if (globals.Bundle !== void 0) m.set("Bundle", globals.Bundle);
  if (globals.ThisDataColl !== void 0) m.set("ThisDataColl", globals.ThisDataColl);
  if (globals.user !== void 0) m.set("user", globals.user);
  return m;
}

// src/sandbox/ScopeBinder.ts
function bindScopeLocals(scope) {
  const locals = {};
  if (scope.locals) {
    for (const k of Object.keys(scope.locals)) {
      locals[k] = scope.locals[k];
    }
  }
  if (scope.kind === "object") {
    if (scope.self !== void 0) {
      locals["self"] = scope.self;
      locals["This"] = scope.This ?? scope.self;
    } else if (scope.This !== void 0) {
      locals["This"] = scope.This;
      locals["self"] = scope.This;
    }
  }
  if (scope.kind === "collection" && scope.ThisDataColl !== void 0) {
    locals["ThisDataColl"] = scope.ThisDataColl;
  }
  return Object.freeze(locals);
}

// src/sandbox/ForbiddenIdentifiers.ts
var CONTEXTUAL_FORBIDDEN_IDENTIFIERS = /* @__PURE__ */ new Set([
  "parent",
  "top",
  "frames",
  "location",
  "history",
  "screen",
  "navigator"
]);
var FORBIDDEN_IDENTIFIERS = /* @__PURE__ */ new Set([
  // DOM / Window
  "document",
  "window",
  "globalThis",
  ...CONTEXTUAL_FORBIDDEN_IDENTIFIERS,
  // self queda fuera de aqui: se resuelve dinamicamente segun el scope (ver sandbox/ScopeProxy).
  // Storage
  "localStorage",
  "sessionStorage",
  "indexedDB",
  // Red nativa
  "fetch",
  "XMLHttpRequest",
  "WebSocket",
  "EventSource",
  "BroadcastChannel",
  // Timers globales (sustituidos por wrappers en `ui`)
  "setTimeout",
  "setInterval",
  "clearTimeout",
  "clearInterval",
  "requestAnimationFrame",
  "cancelAnimationFrame",
  "requestIdleCallback",
  "cancelIdleCallback",
  // Modulos / eval
  "require",
  // 'import' es palabra reservada para `import()` dinamico; se detecta como ImportExpression
  // en el AST (ver parsing/detectForbidden.ts). Aqui se mantiene como identifier por si el
  // script intenta `var x = import;`.
  "eval",
  "Function",
  // Dialogs nativos
  "alert",
  "confirm",
  "prompt",
  // Listeners DOM
  "addEventListener",
  "removeEventListener",
  "dispatchEvent",
  // Workers
  "Worker",
  "SharedWorker",
  "ServiceWorker",
  "MessageChannel",
  "MessagePort"
]);
var ALLOWED_MEMBER_ACCESSORS = /* @__PURE__ */ new Set([
  "appData",
  "ui",
  "self",
  "This",
  "ThisDataColl",
  "$http",
  "crypto",
  "console",
  "Bundle"
]);

// src/sandbox/ScopeProxy.ts
var SAFE_BUILTINS = /* @__PURE__ */ new Set([
  // Tipos primitivos y wrappers.
  "Object",
  "Array",
  "String",
  "Number",
  "Boolean",
  "Symbol",
  "BigInt",
  // Estructuras nucleo.
  "Map",
  "Set",
  "WeakMap",
  "WeakSet",
  "Date",
  "RegExp",
  "Math",
  "JSON",
  "Promise",
  // Errores.
  "Error",
  "TypeError",
  "RangeError",
  "SyntaxError",
  "ReferenceError",
  "EvalError",
  "URIError",
  // Buffers/typed arrays utiles.
  "ArrayBuffer",
  "DataView",
  "Uint8Array",
  "Uint16Array",
  "Uint32Array",
  "Int8Array",
  "Int16Array",
  "Int32Array",
  "Float32Array",
  "Float64Array",
  "Uint8ClampedArray",
  // Funciones globales puras.
  "isNaN",
  "isFinite",
  "parseFloat",
  "parseInt",
  "NaN",
  "Infinity",
  "undefined",
  "encodeURI",
  "encodeURIComponent",
  "decodeURI",
  "decodeURIComponent",
  // Reflect/Proxy (no son DOM y son utiles para metaprogramacion segura).
  "Reflect",
  "Proxy",
  // Iteradores.
  "Iterator"
]);
function buildScopeProxy(args) {
  const { globals, extraGlobals, localBindings } = args;
  const target = /* @__PURE__ */ Object.create(null);
  const handler = {
    // `has` siempre `true` para canalizar TODO acceso por el proxy. Detalle critico
    // para el aislamiento (ver test scopeIsolation.test.ts).
    has(_t, _key) {
      return true;
    },
    get(_t, key) {
      if (typeof key === "symbol") {
        return void 0;
      }
      if (FORBIDDEN_IDENTIFIERS.has(key) && !globals.has(key)) {
        throw new XoneScriptError(
          `Acceso prohibido al identificador "${key}". Las APIs DOM/red estan vetadas en el sandbox XOne web.`,
          { code: XONE_ERROR_CODES.FORBIDDEN_GLOBAL }
        );
      }
      if (Object.prototype.hasOwnProperty.call(localBindings, key)) {
        return localBindings[key];
      }
      if (globals.has(key)) {
        return globals.get(key);
      }
      if (extraGlobals.has(key)) {
        return void 0;
      }
      if (Object.prototype.hasOwnProperty.call(target, key)) {
        return target[key];
      }
      if (SAFE_BUILTINS.has(key)) {
        return globalThis[key];
      }
      return void 0;
    },
    set(_t, key, value) {
      if (typeof key !== "string") return false;
      if (FORBIDDEN_IDENTIFIERS.has(key)) {
        throw new XoneScriptError(
          `Asignacion prohibida sobre el identificador "${key}".`,
          { code: XONE_ERROR_CODES.FORBIDDEN_GLOBAL }
        );
      }
      if (globals.has(key)) {
        return false;
      }
      target[key] = value;
      return true;
    },
    deleteProperty(_t, key) {
      if (typeof key !== "string") return false;
      if (FORBIDDEN_IDENTIFIERS.has(key)) {
        throw new XoneScriptError(
          `Borrado prohibido sobre el identificador "${key}".`,
          { code: XONE_ERROR_CODES.FORBIDDEN_GLOBAL }
        );
      }
      delete target[key];
      return true;
    },
    // Para que `var x = ...` en sloppy mode pueda crear bindings, debemos permitir
    // tambien defineProperty. `with(p)` puede usar este path con engines que
    // implementen Reflect.set via defineProperty para asignacion limpia.
    defineProperty(_t, key, desc) {
      if (typeof key !== "string") return false;
      if (FORBIDDEN_IDENTIFIERS.has(key)) return false;
      return Reflect.defineProperty(target, key, desc);
    },
    getOwnPropertyDescriptor(_t, key) {
      if (typeof key !== "string") return void 0;
      if (Object.prototype.hasOwnProperty.call(target, key)) {
        return Reflect.getOwnPropertyDescriptor(target, key);
      }
      return void 0;
    }
  };
  return new Proxy(target, handler);
}

// src/parsing/parseScript.ts
import * as acorn from "acorn";
function parseScript(source) {
  try {
    return acorn.parse(source, {
      ecmaVersion: "latest",
      sourceType: "script",
      allowAwaitOutsideFunction: true,
      allowReturnOutsideFunction: true,
      locations: true
    });
  } catch (err) {
    const cause = err instanceof Error ? err : new Error(String(err));
    throw new XoneScriptError(
      `Error al parsear el script XOne: ${cause.message}`,
      {
        code: XONE_ERROR_CODES.PARSE_ERROR,
        script: source,
        cause
      }
    );
  }
}

// src/parsing/detectForbidden.ts
import * as acornWalk from "acorn-walk";
function detectForbidden(ast, source, allowEval = false) {
  const baseSet = new Set(
    [...FORBIDDEN_IDENTIFIERS].filter(
      (id) => !CONTEXTUAL_FORBIDDEN_IDENTIFIERS.has(id)
    )
  );
  const effective = allowEval ? new Set([...baseSet].filter((id) => id !== "eval" && id !== "Function")) : baseSet;
  const reportViolation = (name, node) => {
    const loc = node.loc;
    const location = loc ? ` (linea ${loc.start.line}, columna ${loc.start.column})` : "";
    throw new XoneScriptError(
      `Acceso prohibido al identificador "${name}"${location}. Las APIs DOM/red estan vetadas en el sandbox XOne web.`,
      {
        code: XONE_ERROR_CODES.FORBIDDEN_GLOBAL,
        script: source
      }
    );
  };
  acornWalk.ancestor(ast, {
    Identifier(node, _state, ancestors) {
      const id = node;
      if (!effective.has(id.name)) return;
      const parent = ancestors[ancestors.length - 2];
      if (!parent) {
        reportViolation(id.name, id);
        return;
      }
      if (parent.type === "MemberExpression") {
        const m = parent;
        if (!m.computed && m.property === id) {
          return;
        }
      }
      if (parent.type === "Property") {
        const p = parent;
        if (!p.computed && p.key === id) {
          return;
        }
      }
      if (parent.type === "VariableDeclarator") {
        const v = parent;
        if (v.id === id) return;
      }
      if (parent.type === "FunctionDeclaration" || parent.type === "FunctionExpression" || parent.type === "ArrowFunctionExpression") {
        const fn = parent;
        if (fn.id === id) {
          return;
        }
        if (fn.params.includes(id)) {
          return;
        }
      }
      if (parent.type === "ClassDeclaration" || parent.type === "ClassExpression") {
        const cls = parent;
        if (cls.id === id) return;
      }
      if (parent.type === "CatchClause") {
        const cc = parent;
        if (cc.param === id) return;
      }
      if (parent.type === "LabeledStatement" || parent.type === "BreakStatement" || parent.type === "ContinueStatement") {
        return;
      }
      reportViolation(id.name, id);
    },
    ImportExpression(node) {
      reportViolation("import", node);
    },
    ImportDeclaration(node) {
      reportViolation("import", node);
    }
  });
}

// src/parsing/autoAwaitTransformer.ts
import * as acornWalk2 from "acorn-walk";
var ASYNC_METHODS_BY_RECEIVER = /* @__PURE__ */ new Map([
  [
    "ui",
    /* @__PURE__ */ new Set([
      "msgBox",
      "msgBoxWithSound",
      "confirm",
      "prompt",
      "showWaitDialog",
      "showDatePicker",
      "showTimePicker",
      "showSnackbar",
      "sleep",
      "startGps",
      "checkGPSStatus",
      "askUserForGPSPermission",
      "startCamera",
      "captureImage",
      "startSignature",
      "signDataObject",
      "saveDrawing",
      "sendMail",
      "sendSMS",
      "makePhoneCall",
      "pickFile",
      "openFile",
      "openUrl",
      "shareData",
      "playSound",
      "speak",
      "recognizeSpeech",
      "startAudioRecord",
      "stopAudioRecord",
      "showNotification",
      "startPrint",
      "print",
      "printLine",
      "endPrint",
      "startReplica",
      "stopReplica",
      "restartApp",
      "quitApp",
      "showToast",
      "openMenu",
      "openWindow",
      "refresh",
      // `relayout`, `getLanguage`, `getCountryCode` son sincronos (README §5b.2).
      "takePicture",
      "getGPSCoordinates",
      "openSignature"
    ])
  ],
  [
    "$http",
    /* @__PURE__ */ new Set(["get", "post", "put", "delete", "del", "patch", "download"])
  ],
  [
    "self",
    /* @__PURE__ */ new Set(["save", "deleteObject", "refresh", "executeNode", "loadFromJson"])
  ],
  [
    "This",
    /* @__PURE__ */ new Set(["save", "deleteObject", "refresh", "executeNode", "loadFromJson"])
  ],
  [
    "appData",
    /* @__PURE__ */ new Set([
      "login",
      "logout",
      "exit",
      "restart",
      "executeSql",
      "encryptString",
      "decryptString",
      "loadIncludeFile",
      "loadCssFile",
      "unloadCssFile",
      "createCollection",
      "registerPush"
    ])
  ]
]);
function autoAwaitTransformer(source) {
  let ast;
  try {
    ast = parseScript(source);
  } catch {
    return source;
  }
  const insertions = [];
  acornWalk2.ancestor(ast, {
    CallExpression(node, _state, ancestors) {
      const call = node;
      if (call.callee.type !== "MemberExpression") return;
      const m = call.callee;
      if (m.computed) return;
      if (m.object.type !== "Identifier") return;
      if (m.property.type !== "Identifier") return;
      const receiver = m.object.name;
      const method = m.property.name;
      const asyncSet = ASYNC_METHODS_BY_RECEIVER.get(receiver);
      if (!asyncSet || !asyncSet.has(method)) return;
      const parent = ancestors[ancestors.length - 2];
      if (parent && parent.type === "AwaitExpression") return;
      insertions.push(call.start);
    }
  });
  if (insertions.length === 0) return source;
  insertions.sort((a, b) => b - a);
  let out = source;
  for (const pos of insertions) {
    out = out.slice(0, pos) + "await " + out.slice(pos);
  }
  return out;
}

// src/factory/createXoneRuntime.ts
function createXoneRuntime(config) {
  if (!config || typeof config !== "object") {
    throw new TypeError("createXoneRuntime requiere un objeto de configuracion.");
  }
  if (!config.globals) {
    throw new TypeError("XoneRuntimeConfig.globals es obligatorio.");
  }
  const globalsMap = buildGlobalsMap(config.globals);
  const extraGlobals = new Set(config.extraGlobals ?? []);
  const includeSources = /* @__PURE__ */ new Map();
  const useShadowRealm = config.useShadowRealm === true;
  const allowEval = config.allowEval === true;
  const autoAwait = config.autoAwait === true;
  const buildAdapter = () => {
    if (useShadowRealm) {
      const shadow = new ShadowRealmAdapter();
      if (shadow.isAvailable()) return shadow;
    }
    return new AsyncFunctionRealmAdapter(
      Array.from(includeSources.values())
    );
  };
  const runtime = {
    forbiddenIdentifiers: FORBIDDEN_IDENTIFIERS,
    getGlobal(name) {
      return globalsMap.get(name);
    },
    loadIncludeFile(virtualPath, source) {
      if (typeof virtualPath !== "string" || virtualPath.length === 0) {
        throw new TypeError("virtualPath debe ser un string no vacio.");
      }
      if (typeof source !== "string") {
        throw new TypeError("source debe ser un string.");
      }
      const ast = parseScript(source);
      detectForbidden(ast, source, allowEval);
      includeSources.set(virtualPath, source);
    },
    async executeScript(rawSource, scope) {
      if (typeof rawSource !== "string") {
        throw new TypeError("source debe ser un string.");
      }
      if (!scope || typeof scope !== "object") {
        throw new TypeError("scope debe ser un XoneScope.");
      }
      const source = autoAwait ? autoAwaitTransformer(rawSource) : rawSource;
      let ast;
      try {
        ast = parseScript(source);
      } catch (err) {
        const xerr = err instanceof XoneScriptError ? err : new XoneScriptError(
          `Error inesperado al parsear: ${err instanceof Error ? err.message : String(err)}`,
          {
            code: XONE_ERROR_CODES.PARSE_ERROR,
            script: source,
            cause: err instanceof Error ? err : void 0
          }
        );
        config.onUnhandledError?.(xerr);
        throw xerr;
      }
      try {
        detectForbidden(ast, source, allowEval);
      } catch (err) {
        if (err instanceof XoneScriptError) {
          config.onUnhandledError?.(err);
          throw err;
        }
        throw err;
      }
      if (scope.kind === "collection" && scope.self !== void 0 && scope.ThisDataColl === void 0) {
      }
      const localBindings = bindScopeLocals(scope);
      const scopeProxy = buildScopeProxy({
        globals: globalsMap,
        extraGlobals,
        localBindings
      });
      const adapter = buildAdapter();
      try {
        return await adapter.execute(source, scopeProxy);
      } catch (err) {
        const xerr = err instanceof XoneScriptError ? err : new XoneScriptError(
          `Error en tiempo de ejecucion: ${err instanceof Error ? err.message : String(err)}`,
          {
            code: XONE_ERROR_CODES.RUNTIME_ERROR,
            script: source,
            cause: err instanceof Error ? err : void 0
          }
        );
        config.onUnhandledError?.(xerr);
        throw xerr;
      }
    }
  };
  return Object.freeze(runtime);
}

// src/globals/ConsoleAdapter.ts
function createConsole(target) {
  const sink = target ?? (typeof console !== "undefined" ? {
    log: console.log.bind(console),
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  } : {});
  const noop = () => {
  };
  return Object.freeze({
    log: sink.log ?? noop,
    debug: sink.debug ?? sink.log ?? noop,
    info: sink.info ?? sink.log ?? noop,
    warn: sink.warn ?? sink.log ?? noop,
    error: sink.error ?? sink.log ?? noop
  });
}
export {
  ALLOWED_MEMBER_ACCESSORS,
  AsyncFunctionRealmAdapter,
  FORBIDDEN_IDENTIFIERS,
  ShadowRealmAdapter,
  XONE_ERROR_CODES,
  XoneScriptError,
  autoAwaitTransformer,
  bindScopeLocals,
  buildGlobalsMap,
  buildScopeProxy,
  createConsole,
  createXoneRuntime,
  detectForbidden,
  parseScript
};
//# sourceMappingURL=index.js.map